import { useGetAttachmentsByDocId } from "@/api/attachment";
import { getCollectors } from "@/api/collector/collector";
import {
  useDeleteSculptureEdition,
  useGetSculptureEditionById,
  useUpdateSculptureEdition,
} from "@/api/sculpture-edition/sculpture-edition";
import * as AF from "@/components/form-items/AbstractForm";
import { FourDimensionalInput } from "@/components/form-items/DimensionsInput";
import { AbstractFormSelectFetcher } from "@/components/form-items/select-fetcher-form";
import { ImageFormData } from "@/components/images/MediaLibrary";
import LinksManagement from "@/components/LinksManagement";
import { Form } from "@/components/ui/form";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/context/auth/AuthContext";
import { CurrentTabProvider } from "@/context/CurrentTabContext";
import {
  FormFieldVisibilityProvider,
  useFormFieldVisibility,
} from "@/context/FormFieldVisibilityContext";
import { FormVisibilityMenuItems } from "@/components/form-items/FormVisibilityMenuItems";
import { toast } from "@/hooks/use-toast";
import { getDefaultImage } from "@/lib/image";
import { normalizeMoneyAmount } from "@/lib/shared-types";
import type { Collector } from "@/lib/types";
import * as Tabs from "@radix-ui/react-tabs";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getSculptureEditionTabs,
  sculptureEditionSubmitOmitKeys,
} from "./edition-sheet/constants";
import SculptureEditionCertificatTab from "./edition-sheet/SculptureEditionCertificatTab";
import SculptureEditionConditionReportTab from "./edition-sheet/SculptureEditionConditionReportTab";
import SculptureEditionInformationsTab from "./edition-sheet/SculptureEditionInformationsTab";
import SculptureEditionSheetHeader from "./edition-sheet/SculptureEditionSheetHeader";
import SculptureEditionTechnicalNotesTab from "./edition-sheet/SculptureEditionTechnicalNotesTab";
import SculptureEditionHistoryTable from "./SculptureEditionHistoryTable";
import SculptureEditionMouvementTable from "./SculptureEditionMouvementTable";

interface SculptureEditionSheetFormProps {
  editionId: string;
  artworkTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

const MONEY_AMOUNT_FIELDS = [
  "sculptureEditionEstimationMontant",
  "sculptureEditionAssuranceMontant",
  "sculptureEditionVenteMontant",
  "sculptureEditionAcquisitionMontant",
] as const;

const DIMENSION_FIELDS = [
  "sculptureEditionDimensions",
  "sculptureEditionDimensionsSocle",
  "sculptureEditionDimensionsBrutes",
  "sculptureEditionDimensionsAvecSocle",
] as const;

const parseDimensionField = (value: unknown) => {
  if (!value || typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const SculptureEditionSheetFormInner = ({
  editionId,
  artworkTitle,
  open,
  onOpenChange,
  onDeleted,
}: SculptureEditionSheetFormProps) => {
  const { t } = useTranslation();
  const { isTabVisible } = useFormFieldVisibility();
  const queryClient = useQueryClient();
  const { user, instanceData } = useAuth();
  const isReader = user?.role === "reader" || false;

  const { data: editionData, isLoading } =
    useGetSculptureEditionById(editionId);
  const { mutateAsync: updateEdition } = useUpdateSculptureEdition();
  const { mutateAsync: deleteEdition } = useDeleteSculptureEdition();
  const { data: editionAttachments } = useGetAttachmentsByDocId(editionId);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMediaDrawerOpen, setIsMediaDrawerOpen] = useState(false);

  const defaultEditionImage = useMemo(
    () =>
      editionAttachments
        ? getDefaultImage(editionAttachments, "artwork")
        : undefined,
    [editionAttachments],
  );

  const computeEditionImageName = (_data?: ImageFormData) => {
    return editionId ?? "";
  };

  const handleInvalidateAttachments = () => {
    queryClient.invalidateQueries({ queryKey: ["attachments", editionId] });
  };

  const defaultValues = useMemo(() => {
    const values: Record<string, unknown> = {
      ...editionData,
      sculptureEditionNumeroEnregistrement:
        editionData?.sculptureEditionNumeroEnregistrement ?? "",
    };
    DIMENSION_FIELDS.forEach((field) => {
      values[field] = parseDimensionField(values[field]);
    });
    MONEY_AMOUNT_FIELDS.forEach((field) => {
      const normalized = normalizeMoneyAmount(values[field]);
      if (normalized) {
        values[field] = normalized;
      }
    });
    return values;
  }, [editionData]);

  const form = useForm<FieldValues>({
    reValidateMode: "onSubmit",
    defaultValues,
  });

  const hasInitialized = useRef(false);
  const lastEditionId = useRef<string | undefined>(editionId);
  const lastOpenState = useRef<boolean>(open);

  useEffect(() => {
    const idChanged = lastEditionId.current !== editionId;
    const sheetJustOpened = open && !lastOpenState.current;

    if (idChanged || sheetJustOpened) {
      hasInitialized.current = false;
      lastEditionId.current = editionId;
    }
    lastOpenState.current = open;

    const shouldWaitForData = editionId && isLoading;
    const dataIsReady = editionId ? !isLoading : true;

    if (
      !hasInitialized.current &&
      !shouldWaitForData &&
      dataIsReady &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      form.reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [defaultValues, form, editionId, open, editionData, isLoading]);

  const onSubmit = async (
    data: FieldValues,
    publishState: "draft" | "published" = "draft",
  ) => {
    if (!editionId) return;

    const submitData: Record<string, unknown> = {};
    Object.keys(data).forEach((key) => {
      if (
        !sculptureEditionSubmitOmitKeys.includes(
          key as (typeof sculptureEditionSubmitOmitKeys)[number],
        ) &&
        data[key] !== undefined
      ) {
        if (
          DIMENSION_FIELDS.includes(key as (typeof DIMENSION_FIELDS)[number]) &&
          data[key] !== null &&
          typeof data[key] === "object"
        ) {
          submitData[key] = JSON.stringify(data[key]);
        } else {
          submitData[key] = data[key];
        }
      }
    });

    submitData["sculptureEditionPublicationStatus"] = publishState;

    try {
      await updateEdition({
        data: submitData,
        where: { id: editionId },
      });
      toast({
        title: t("recordSuccessfullyUpdated"),
        duration: 3000,
        variant: "success",
      });
    } catch (err) {
      if (
        isAxiosError(err) &&
        (err.response?.status === 409 || err.response?.status === 422)
      ) {
        form.setError("sculptureEditionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      toast({
        title: t("errorUpdatingRecord"),
        description: isAxiosError(err) ? err.message : t("unknownError"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!editionId) return;

    try {
      await deleteEdition(editionId);
      toast({
        title: t("recordSuccessfullyDeleted"),
        duration: 3000,
        variant: "success",
      });
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t("errorDeletingRecord"),
        description: isAxiosError(err) ? err.message : t("unknownError"),
        variant: "destructive",
      });
    }
  };

  const watchedNumeroEnregistrement = form.watch(
    "sculptureEditionNumeroEnregistrement",
  );

  const formattedSheetTitle = useMemo(() => {
    const numero = watchedNumeroEnregistrement ?? "";
    const base =
      `${artworkTitle} - ${t("sculpture.form.editionKeyLabelTitleForm")} ${numero}`.trim();
    return base;
  }, [artworkTitle, t, watchedNumeroEnregistrement]);

  const provenanceMode = instanceData?.provenanceMode ?? false;
  const tabs: { value: string; label: string; nonHideable?: boolean }[] =
    getSculptureEditionTabs(t, { provenanceMode }).map((tab, i) =>
      i === 0 ? { ...tab, nonHideable: true } : tab,
    );

  const [activeTab, setActiveTab] = useState(tabs[0]?.value ?? "informations");

  // Tabs the user chose to hide through the visibility grid (1st tab stays).
  const visibleTabs = useMemo(
    () => tabs.filter((tab) => tab.nonHideable || isTabVisible(tab.value)),
    // `tabs` is rebuilt each render; `isTabVisible` changes with the grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabVisible, provenanceMode, t],
  );

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === activeTab) && visibleTabs[0]) {
      setActiveTab(visibleTabs[0].value);
    }
  }, [visibleTabs, activeTab]);

  if (isLoading && editionId) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[800px] px-0">
          <div className="flex items-center justify-center h-full">
            <p>{t("loading")}</p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="sm:max-w-6xl w-full px-0 max-h-screen overflow-y-auto flex flex-col"
        closeIconLeft={false}
        showPrimitiveCross={false}
      >
        <SculptureEditionSheetHeader
          isReader={isReader}
          isDeleteDialogOpen={isDeleteDialogOpen}
          setIsDeleteDialogOpen={setIsDeleteDialogOpen}
          formattedSheetTitle={formattedSheetTitle}
          publicationStatus={editionData?.sculptureEditionPublicationStatus}
          onDelete={handleDelete}
          onSaveDraft={form.handleSubmit((data) => onSubmit(data, "draft"))}
          onSavePublished={form.handleSubmit((data) =>
            onSubmit(data, "published"),
          )}
          t={t}
          actionsMenuExtra={
            <FormVisibilityMenuItems tabs={tabs} activeTab={activeTab} />
          }
        />

        <Form {...form}>
          <Tabs.Root
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex flex-col flex-1 min-h-0"
          >
            <Tabs.TabsList className="flex flex-row gap-1 px-8 pb-2 border-b flex-shrink-0 ">
              <div className="bg-[#F1F5F9] rounded-md p-1">
                {visibleTabs.map((tab) => (
                  <Tabs.TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="px-3 py-1 rounded-md text-[12px] font-medium transition-colors data-[state=active]:bg-white data-[state=active]:text-gray-700  data-[state=inactive]:text-gray-700 hover:text-foreground"
                  >
                    {tab.label}
                  </Tabs.TabsTrigger>
                ))}
              </div>
            </Tabs.TabsList>

            <CurrentTabProvider value={activeTab}>
            <div className="flex-1 overflow-y-auto">
              <Tabs.TabsContent
                value="informations"
                className="px-11 py-5 gap-8"
              >
                <SculptureEditionInformationsTab
                  editionId={editionId}
                  masterArtworkId={editionData?.masterArtworkId ?? undefined}
                  defaultEditionImage={defaultEditionImage ?? undefined}
                  editionAttachments={editionAttachments}
                  isMediaDrawerOpen={isMediaDrawerOpen}
                  setIsMediaDrawerOpen={setIsMediaDrawerOpen}
                  handleInvalidateAttachments={handleInvalidateAttachments}
                  computeEditionImageName={computeEditionImageName}
                  t={t}
                />
              </Tabs.TabsContent>

              <Tabs.TabsContent value="dimensions" className="px-11 py-5 gap-8">
                <AF.Col>
                  {/* 1. Dimensions */}
                  <FourDimensionalInput
                    parentName="sculptureEditionDimensions"
                    needVolume={true}
                    needSurface={false}
                  />
                  <AF.Text
                    className="w-20"
                    name="sculptureEditionPoids"
                    label={t("sculpture.edition.poids")}
                    unit="kg"
                  />

                  {/* 2. Dimensions du socle */}
                  <FourDimensionalInput
                    parentName="sculptureEditionDimensionsSocle"
                    needVolume={true}
                    needSurface={false}
                  />
                  <AF.Text
                    className="w-20"
                    name="sculptureEditionPoidsSocle"
                    label={t("sculpture.edition.poids-socle")}
                    unit="kg"
                  />

                  {/* 3. Dimensions brutes */}
                  <FourDimensionalInput
                    parentName="sculptureEditionDimensionsBrutes"
                    needVolume={true}
                    needSurface={false}
                  />
                  <AF.Text
                    className="w-20"
                    name="sculptureEditionPoidsBrutes"
                    label={t("sculpture.edition.poids-brutes")}
                    unit="kg"
                  />

                  {/* 4. Dimensions avec socle/piedouche */}
                  <FourDimensionalInput
                    parentName="sculptureEditionDimensionsAvecSocle"
                    needVolume={true}
                    needSurface={false}
                  />
                  <AF.Text
                    className="w-20"
                    name="sculptureEditionPoidsSoclePiedouche"
                    label={t("sculpture.edition.poids-socle-piedouche")}
                    unit="kg"
                  />

                  {/* 5. Dimensions personnalisées */}
                  <AF.Text
                    name="sculptureEditionDimensionsPersonnalisees"
                    label={t("sculpture.edition.dimensions-personnalisees")}
                    sublabel={t(
                      "sculpture.edition.dimensions-personnalisees-sublabel",
                    )}
                  />
                  <AF.Text
                    name="sculptureEditionPoidsLibre"
                    label={t("sculpture.edition.poids-libre")}
                  />

                  {/* 6. Notes poids */}
                  <AF.RichText
                    name="sculptureEditionNotesPoids"
                    label={t("sculpture.edition.notes-poids")}
                  />
                </AF.Col>
              </Tabs.TabsContent>

              <Tabs.TabsContent value="historique" className="px-11 py-5 gap-8">
                <AF.Col>
                  <AbstractFormSelectFetcher
                    label={t("collector")}
                    name="collectorId"
                    fetcher={async () => await getCollectors({})}
                    queryKey={["collectors"]}
                    allowClear={true}
                    getContent={(collector: Collector) => (
                      <>
                        {collector.collectionneursDescriptionNom}{" "}
                        {collector.collectionneursDescriptionPrenom}{" "}
                        {collector.collectionneursDescriptionVille
                          ? `(${collector.collectionneursDescriptionVille})`
                          : ""}
                      </>
                    )}
                    getLabel={(collector: Collector) =>
                      `${collector.collectionneursDescriptionNom} ${collector.collectionneursDescriptionPrenom} ${collector.collectionneursDescriptionVille ? `(${collector.collectionneursDescriptionVille})` : ""}`
                    }
                    getValue="id"
                  />
                  <AF.Checkbox
                    name="nonLocalise"
                    label={t("historique.sculpture.collection.nonLocalise")}
                  />
                  <SculptureEditionHistoryTable
                    sculptureEditionId={editionId}
                    disabled={isReader}
                    provenanceMode={provenanceMode}
                  />
                </AF.Col>
              </Tabs.TabsContent>

              <Tabs.TabsContent value="links" className="px-11 py-5 gap-8">
                {editionId && (
                  <LinksManagement
                    type="sculptureEdition"
                    className="pr-0"
                    id={editionId}
                    data={editionData as any}
                    configuration={{
                      fields: [
                        {
                          name: "links",
                          type: "links",
                          label: t("linkedEntries"),
                          links: [
                            {
                              name: "relatedArtwork",
                              label: t("entryartworks"),
                              type: "link-simple",
                            },
                            {
                              name: "relatedMultipleprint",
                              label: t("entryMultiples"),
                              type: "link-simple",
                            },
                            {
                              name: "sculptureEditionInExhibitions",
                              label: t("entryexhibitions"),
                              type: "link-complex",
                              fields: [
                                {
                                  name: "exhibition.expositionsDescriptionNumeroExpo",
                                  label: t("exhibitionDescriptionNumeroExpo"),
                                  type: "text",
                                  className: "w-full",
                                  disabled: true,
                                },
                                {
                                  name: "exhibition.expositionsDescriptionResume",
                                  label: t("exhibitionDescriptionResume"),
                                  type: "richtext",
                                  className: "w-full",
                                  disabled: true,
                                },
                                {
                                  name: "sculptureEditionDansExpositionsNumeroCatalogue",
                                  label: t(
                                    "sculptureEditionDansExpositionsNumeroCatalogue",
                                  ),
                                  type: "text",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansExpositionsNotes",
                                  label: t(
                                    "sculptureEditionDansExpositionsNotes",
                                  ),
                                  type: "richtext",
                                  className: "w-full",
                                },
                              ],
                            },
                            {
                              name: "sculptureEditionInBibliography",
                              label: t("entrybibliographies"),
                              type: "link-complex",
                              fields: [
                                {
                                  type: "row",
                                  className: "items-start w-full",
                                  children: [
                                    {
                                      name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                                      label: t(
                                        "bibliographieDescriptionNumeroBibliographie",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                      disabled: true,
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "items-start w-full",
                                  children: [
                                    {
                                      name: "bibliography.bibliographieDescriptionResumeLong",
                                      label: t(
                                        "bibliographieDescriptionResumeLong",
                                      ),
                                      type: "richtext",
                                      className: "w-full",
                                      disabled: true,
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "grid grid-cols-2 w-full gap-4",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographieReproduitPage",
                                      label: t(
                                        "sculptureEditionDansBibliographieReproduitPage",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                    },
                                    {
                                      name: "sculptureEditionDansBibliographieReproduitPlanche",
                                      label: t(
                                        "sculptureEditionDansBibliographieReproduitPlanche",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className:
                                    "grid grid-cols-2 w-full -mt-3 pt-0",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographiePlancheNonNumerotee",
                                      label: t(
                                        "sculptureEditionDansBibliographiePlancheNonNumerotee",
                                      ),
                                      type: "checkbox",
                                      className: "w-full col-start-2",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "grid grid-cols-2 w-full gap-4",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographieCitePage",
                                      label: t(
                                        "sculptureEditionDansBibliographieCitePage",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "grid grid-cols-2 w-full gap-4",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographieListePage",
                                      label: t(
                                        "sculptureEditionDansBibliographieListePage",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                    },
                                    {
                                      name: "sculptureEditionDansBibliographieSousNumero",
                                      label: t(
                                        "sculptureEditionDansBibliographieSousNumero",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "items-start w-full",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographiePhotographe",
                                      label: t(
                                        "sculptureEditionDansBibliographiePhotographe",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "grid grid-cols-2 w-full gap-4",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographieNonPagine",
                                      label: t(
                                        "sculptureEditionDansBibliographieNonPagine",
                                      ),
                                      type: "checkbox",
                                      className: "w-full",
                                    },
                                    {
                                      name: "sculptureEditionDansBibliographieReproductionCouverture",
                                      label: t(
                                        "sculptureEditionDansBibliographieReproductionCouverture",
                                      ),
                                      type: "checkbox",
                                      className: "w-full",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "items-start w-full",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographieReproductionCouleur",
                                      label: t(
                                        "sculptureEditionDansBibliographieReproductionCouleur",
                                      ),
                                      type: "checkbox",
                                      className: "w-full",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "items-start w-full",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographieTexteLibre",
                                      label: t(
                                        "sculptureEditionDansBibliographieTexteLibre",
                                      ),
                                      type: "text",
                                      className: "w-full",
                                    },
                                  ],
                                },
                                {
                                  type: "row",
                                  className: "items-start w-full",
                                  children: [
                                    {
                                      name: "sculptureEditionDansBibliographieNotes",
                                      label: t(
                                        "sculptureEditionDansBibliographieNotes",
                                      ),
                                      type: "richtext",
                                      className: "w-full",
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              name: "sculptureEditionInAuction",
                              label: t("entryauctions"),
                              type: "link-complex",
                              fields: [
                                {
                                  name: "auction.ventesDescriptionNumeroVente",
                                  label: t("auctionDescriptionNumeroVente"),
                                  type: "text",
                                  className: "w-full",
                                  disabled: true,
                                },
                                {
                                  name: "auction.ventesDescriptionResume",
                                  label: t("auctionDescriptionResume"),
                                  type: "richtext",
                                  className: "w-full",
                                  disabled: true,
                                },
                                {
                                  name: "sculptureEditionDansVentesNumeroLot",
                                  label: t(
                                    "sculptureEditionDansVentesNumeroLot",
                                  ),
                                  type: "text",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesReproduitPage",
                                  label: t(
                                    "sculptureEditionDansVentesReproduitPage",
                                  ),
                                  type: "text",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesEstimationBasse",
                                  label: t(
                                    "sculptureEditionDansVentesEstimationBasse",
                                  ),
                                  type: "inputSelectCurrency",
                                  className: "w-full",
                                  defaultSelectValue: "EUR",
                                },
                                {
                                  name: "sculptureEditionDansVentesEstimationHaute",
                                  label: t(
                                    "sculptureEditionDansVentesEstimationHaute",
                                  ),
                                  type: "inputSelectCurrency",
                                  className: "w-full",
                                  defaultSelectValue: "EUR",
                                },
                                {
                                  name: "sculptureEditionDansVentesVendu",
                                  label: t("sculptureEditionDansVentesVendu"),
                                  type: "checkbox",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesResultatNonListe",
                                  label: t(
                                    "sculptureEditionDansVentesResultatNonListe",
                                  ),
                                  type: "checkbox",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesNonVenu",
                                  label: t("sculptureEditionDansVentesNonVenu"),
                                  type: "checkbox",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesNonVendu",
                                  label: t(
                                    "sculptureEditionDansVentesNonVendu",
                                  ),
                                  type: "checkbox",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesNonReproduit",
                                  label: t(
                                    "sculptureEditionDansVentesNonReproduit",
                                  ),
                                  type: "checkbox",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesRetire",
                                  label: t("sculptureEditionDansVentesRetire"),
                                  type: "checkbox",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesMiseAPrix",
                                  label: t(
                                    "sculptureEditionDansVentesMiseAPrix",
                                  ),
                                  type: "inputSelectCurrency",
                                  defaultSelectValue: "EUR",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesPrixMarteau",
                                  label: t(
                                    "sculptureEditionDansVentesPrixMarteau",
                                  ),
                                  type: "inputSelectCurrency",
                                  defaultSelectValue: "EUR",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesPrixAvecFrais",
                                  label: t(
                                    "sculptureEditionDansVentesPrixAvecFrais",
                                  ),
                                  type: "inputSelectCurrency",
                                  defaultSelectValue: "EUR",
                                  className: "w-full",
                                },
                                {
                                  name: "sculptureEditionDansVentesNotes",
                                  label: t("sculptureEditionDansVentesNotes"),
                                  type: "richtext",
                                  className: "w-full",
                                },
                              ],
                            },
                            {
                              name: "relatedAudiovisual",
                              label: t("entryAudiovisuals"),
                              type: "link-simple",
                            },
                            {
                              name: "relatedDocument",
                              label: t("entryDocuments"),
                              type: "link-simple",
                            },
                            {
                              name: "relatedWriting",
                              label: t("entryWritings"),
                              type: "link-simple",
                            },
                            {
                              name: "relatedPhotography",
                              label: t("entryPhotographies"),
                              type: "link-simple",
                            },
                            {
                              name: "externalLinks",
                              label: t("entryExternalLinks"),
                              type: "link-simple",
                            },
                          ],
                        },
                      ],
                    }}
                  />
                )}
              </Tabs.TabsContent>

              <Tabs.TabsContent
                value="technicalNotes"
                className="px-11 py-5 gap-8"
              >
                <SculptureEditionTechnicalNotesTab />
              </Tabs.TabsContent>

              <Tabs.TabsContent
                value="conditionReport"
                className="px-11 py-5 gap-8"
              >
                <SculptureEditionConditionReportTab />
              </Tabs.TabsContent>

              <Tabs.TabsContent value="certificat" className="px-11 py-5 gap-8">
                <SculptureEditionCertificatTab />
              </Tabs.TabsContent>

              <Tabs.TabsContent value="mouvement" className="px-11 py-5 gap-8">
                <SculptureEditionMouvementTable
                  sculptureEditionId={editionId}
                  disabled={isReader}
                />
              </Tabs.TabsContent>
            </div>
            </CurrentTabProvider>
          </Tabs.Root>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

const SculptureEditionSheetForm = (props: SculptureEditionSheetFormProps) => (
  <FormFieldVisibilityProvider formKey="sculpture-edition-sheet">
    <SculptureEditionSheetFormInner {...props} />
  </FormFieldVisibilityProvider>
);

export default SculptureEditionSheetForm;
